import {
  User,
  UserActions,
  UserPhotoType,
  topliApi,
  AuthUserRole2 as UserRole,
} from '@/shared/api/topli/TopliApi'
import { CommonCreateUserFormState, CreateUserFormBody, CreateUserFormState } from '../model/types'
import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersByPageQueryKey } from '@/entities/User'
import { convertPhoneStringToNumber } from '@/shared/lib/utils'
import { UploadCroppedPhoto } from '@/entities/Photos/api/photos-front_crop-api'

export const createUser = async (state: CreateUserFormState) => {
  const { phone, fullname, email, ...restState } = state
  const body: CreateUserFormBody = {
    ...restState,
    fullname: fullname,
    username: email.toLocaleLowerCase(),
    email,
    phone: convertPhoneStringToNumber(phone),
  }
  const response = await topliApi.user.create({ item_id: null }, body)
  return response
}

const createTeam = async (id: number, role: UserRole) => {
  if (role !== UserRole.TeamHead) return
  return await topliApi.user.createTeam(id)
}

const createDefaultData = async (id: number) => {
  return await topliApi.user.createDefaultData(id)
}

const createAgentAction = async (id: number, role: UserRole) => {
  if (role === UserRole.Agent || UserRole.TeamHead) {
    return await topliApi.agentActions.create(
      { item_id: null },
      { user_id: id, action: UserActions.RegisterUser },
    )
  }
}

const UploadPhotoAndCreate = async (state: CommonCreateUserFormState) => {
  const { avatar_url, company_logo_url, role } = state
  const body: CreateUserFormState = {
    ...state,
    avatar_url: null,
    company_logo_url: null,
  }

  const response = await createUser(body)
  const { id } = response
  let newState = { ...state, id } as CommonCreateUserFormState

  if (avatar_url && typeof avatar_url === 'object') {
    const result = await UploadCroppedPhoto({ id, file: avatar_url, type: UserPhotoType.Avatar })
    newState = { ...newState, avatar_url: result.avatar_url }
  }

  if (company_logo_url && typeof company_logo_url === 'object') {
    const result = await UploadCroppedPhoto({
      id,
      file: company_logo_url,
      type: UserPhotoType.CompanyLogo,
    })
    newState = { ...newState, company_logo_url: result.company_logo_url }
  }

  await createDefaultData(id)

  await createTeam(id, role)

  await createAgentAction(id, role)

  return response
}

export const useCreateUserApi = (): UseMutationResult<User, Error, CreateUserFormState> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: state => UploadPhotoAndCreate(state),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [usersByPageQueryKey], refetchType: 'active' })
    },
  })
}
